import { createSelect, MountTarget, newDialog, readConfig } from './utils/elements';
import { kadetCircle } from './utils/svg';

export const HEURISTIEK_EDIT_SELECTOR = '.cds-lrn-heuristiek';

interface HeuristiekConfig {
  type: 'alpha' | 'icons';
  amount: number;
}

export function heuristiekDialog(
  insertHtml: (html: string) => void,
  iconPath: string,
  mountTarget?: MountTarget,
  existingEl?: HTMLElement
) {
  const dialog = newDialog('Heuristiek maken', () => insertHtml(''), mountTarget);

  const typeInput = createSelect('type', 'Type', [
    { label: 'Letters', value: 'alpha' },
    { label: 'Icoontjes', value: 'icons' },
  ]);

  const amountInput = createSelect('steps', 'Aantal stappen', [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
  ]);

  if (existingEl) {
    const cfg = readConfig<HeuristiekConfig>(existingEl, { type: 'alpha', amount: 1 });
    if (cfg.type) typeInput.setValue(cfg.type);
    if (cfg.amount) amountInput.setValue(String(cfg.amount));
  }

  dialog.querySelector('#cds-dialog-content').append(typeInput.field, amountInput.field);

  dialog.addEventListener('onSubmit', () => {
    const config: HeuristiekConfig = { type: typeInput.value() as 'alpha' | 'icons', amount: +amountInput.value() };
    insertHtml(_createHeuristiek(config, iconPath));
  });
}

function _createHeuristiek(config: HeuristiekConfig, iconPath: string) {
  const type = config.type;
  const amount = config.amount || 1;
  const paths = type === 'alpha' ? pathsForPart : pathsForIcon(iconPath);

  const container = document.createElement('div');
  container.classList.add('cds-lrn-heuristiek');
  container.setAttribute('data-cb-config', JSON.stringify(config));

  const table = document.createElement('table');
  table.classList.add('w-full');
  container.append(table);

  const dummySymbol = document.createElement('div');
  dummySymbol.innerHTML = kadetCircle;
  container.append(dummySymbol.firstChild as HTMLDivElement);

  for (let i = 0; i < amount; i++) {
    const question = _createHeuristiekItem('V', amount === 1 ? 0 : i + 1, 'Vraag', paths);
    const operation = _createHeuristiekItem('B', amount === 1 ? 0 : i + 1, 'Bewerking', paths);
    const answer = _createHeuristiekItem('A', amount === 1 ? 0 : i + 1, 'Antwoordzin', paths);
    const ok = _createOkIcon(paths);

    const tableBody = document.createElement('tbody');
    if (i > 0) {
      const spacer = document.createElement('tr');
      spacer.classList.add('row-h-xl', 'border-bs');
      spacer.append(document.createElement('td'), document.createElement('td'));

      tableBody.append(spacer);
    }
    [question, operation, answer, ok].forEach((el) => el && tableBody.append(el));

    table.append(tableBody);
  }

  return container.outerHTML;
}

function _createKadetCircle() {
  const iconSvg = document.createElement('svg');
  iconSvg.classList.add('heuristiek-icon', 'absolute', 'inset-0', 'index-content-on-surface');
  const kadetCircleEl = document.createElement('use');
  kadetCircleEl.setAttribute('href', '#kadetcircle');
  iconSvg.appendChild(kadetCircleEl);
  return iconSvg;
}

function _createHeuristiekItem(type: 'V' | 'B' | 'A', index: number, label: string, paths) {
  const part = index === 0 ? type : `${type}${index}`;
  if (!paths[part]) return;

  const iconWrapper = document.createElement('div');
  iconWrapper.classList.add('relative', 'heuristiek-icon');

  let icon;

  if (paths[part].startsWith('https://')) {
    icon = document.createElement('img');
    icon.src = paths[part];
  } else {
    icon = document.createElement('svg');
    icon.setAttribute('viewBox', '0 0 22.24 22.25');
    icon.innerHTML = paths[part];
  }
  icon.classList.add('absolute', 'inset-0', 'index-surface');

  iconWrapper.append(_createKadetCircle(), icon);

  const textElement = document.createElement('div');
  textElement.classList.add('flex-1');
  textElement.textContent = label;

  const row = document.createElement('tr');

  const iconCell = document.createElement('td');
  iconCell.classList.add('w-0', 'row-h-m', 'p-3xs');
  iconCell.append(iconWrapper);

  const textCell = document.createElement('td');
  textCell.classList.add('p-b-3xs', 'p-i-xs');
  textCell.append(textElement);

  row.append(iconCell, textCell);

  return row;
}

function _createOkIcon(paths) {
  const row = document.createElement('tr');
  const okCell = document.createElement('td');
  okCell.setAttribute('colspan', '2');

  let iconOk;
  if (paths.ok.startsWith('https://')) {
    iconOk = document.createElement('img');
    iconOk.src = paths.ok;
  } else {
    iconOk = document.createElement('svg');
    iconOk.setAttribute('viewBox', '0 0 22.24 22.25');
    iconOk.innerHTML = paths.ok;
  }
  iconOk.classList.add('absolute', 'inset-0', 'index-content-on-surface');

  const okIcon = document.createElement('div');
  okIcon.classList.add('relative', 'heuristiek-icon', 'm-is-auto');
  okIcon.append(_createKadetCircle(), iconOk);

  okCell.append(okIcon);
  row.append(okCell);

  return row;
}

const pathsForPart = {
  A: '<path d="M13,15.52c-.17-.6-.35-1.2-.52-1.81-.55-.01-1.07-.01-1.55-.01s-.91,0-1.22.01l-.48,1.73c-.88.01-1.69.04-2.57.08.94-2.59,2.41-7.1,3.08-9.22.48.01.95.03,1.43.03s.96-.01,1.44-.03c1.31,4.28,2.42,7.35,3.07,9.14-.88.01-1.78.04-2.68.08ZM11.09,8.31l-.9,3.58c.22-.01.46-.01.68-.01.44,0,.86.01,1.12.01l-.9-3.58Z" class="fill-primary"></path>',
  A1: '<path d="M11.16,15.52c-.17-.6-.35-1.2-.52-1.81-.55-.01-1.07-.01-1.55-.01s-.91,0-1.22.01l-.48,1.73c-.88.01-1.69.04-2.57.08.94-2.59,2.41-7.1,3.08-9.22.48.01.95.03,1.43.03s.96-.01,1.44-.03c1.31,4.28,2.42,7.35,3.07,9.14-.88.01-1.78.04-2.68.08ZM9.25,8.31l-.9,3.58c.22-.01.46-.01.68-.01.44,0,.86.01,1.12.01l-.9-3.58Z" class="fill-primary"></path><path d="M15.87,16.28c-.46,0-1.19.02-1.62.04v-1.02s.53-.03.99-.05v-2.96h-1.03c.02-.11.03-.27.03-.4,0-.2-.02-.41-.03-.44.71-.06,1.33-.23,1.96-.5h.42v4.3l.82.05v1.02c-.42-.02-1.14-.04-1.54-.04Z" class="fill-primary"></path>',
  A2: '<path d="M10.8,15.52c-.17-.6-.35-1.2-.52-1.81-.55-.01-1.07-.01-1.55-.01s-.91,0-1.22.01l-.48,1.73c-.88.01-1.69.04-2.57.08.94-2.59,2.41-7.1,3.08-9.22.48.01.95.03,1.43.03s.96-.01,1.44-.03c1.31,4.28,2.42,7.35,3.07,9.14-.88.01-1.78.04-2.68.08ZM8.89,8.31l-.9,3.58c.22-.01.46-.01.68-.01.44,0,.86.01,1.12.01l-.9-3.58Z" class="fill-primary"></path><path d="M13.91,15.45c.55-.34,2.24-1.58,2.24-2.68,0-.51-.38-.73-.79-.73-.39,0-.8.22-.99.6h-.05c-.1-.33-.44-.71-.56-.81.3-.4.92-.93,1.89-.93,1.3,0,1.93.71,1.93,1.71s-.8,1.86-1.78,2.65c.55-.04,1.05-.05,1.4-.05.2,0,.41,0,.53,0-.03.34-.03.73-.02,1.05-.06,0-1.18-.02-1.8-.02s-1.86.02-1.99.02v-.83Z" class="fill-primary"></path>',
  A3: '<path d="M11,15.52c-.17-.6-.35-1.2-.52-1.81-.55-.01-1.07-.01-1.55-.01s-.91,0-1.22.01l-.48,1.73c-.88.01-1.69.04-2.57.08.94-2.59,2.41-7.1,3.08-9.22.48.01.95.03,1.43.03s.96-.01,1.44-.03c1.31,4.28,2.42,7.35,3.07,9.14-.88.01-1.78.04-2.68.08ZM9.09,8.31l-.9,3.58c.22-.01.46-.01.68-.01.44,0,.86.01,1.12.01l-.9-3.58Z" class="fill-primary"></path><path d="M16.19,14.54c0-.5-.56-.53-.99-.53h-.16c0-.07.02-.14.02-.21,0-.19-.04-.4-.09-.63.55,0,1.04-.18,1.04-.7,0-.31-.31-.44-.58-.44-.31,0-.68.16-.86.5h-.07c-.11-.33-.46-.71-.58-.8.3-.4.92-.83,1.88-.83s1.61.55,1.61,1.3c0,.45-.24.96-.82,1.21.68.2,1.02.59,1.02,1.28,0,.99-.87,1.62-1.91,1.62-.67,0-1.36-.23-1.74-.5.12-.28.2-.64.23-1.03.27.19.61.43,1.28.43.48,0,.72-.31.72-.67Z" class="fill-primary"></path>',
  B: '<path d="M8.07,8.72c0-.65-.09-2.09-.13-2.41h3.67c1.96,0,3.04,1.12,3.04,2.33,0,.95-.35,1.52-1.2,1.96v.03c1.01.44,1.47,1.25,1.47,2.08,0,1.47-1.16,2.73-3.11,2.73h-3.78s.04-1.07.04-1.55v-5.17ZM11.5,9.89c.64,0,.95-.43.95-.85,0-.57-.36-.91-1.09-.91h-.95c.01.32.01.43.01.74,0,.36,0,.73-.01,1.01h1.09ZM11.47,13.62c.74,0,1.07-.56,1.07-1.03,0-.55-.46-1.01-1.22-1.01h-.91c.01.43.01.79.01,1.18,0,.33,0,.34-.01.86h1.07Z" class="fill-primary"></path>',
  B1: '<path d="M6.23,8.72c0-.65-.09-2.09-.13-2.41h3.67c1.96,0,3.04,1.12,3.04,2.33,0,.95-.35,1.52-1.2,1.96v.03c1.01.44,1.47,1.25,1.47,2.08,0,1.47-1.16,2.73-3.11,2.73h-3.78s.04-1.07.04-1.55v-5.17ZM9.66,9.89c.64,0,.95-.43.95-.85,0-.57-.36-.91-1.09-.91h-.95c.01.32.01.43.01.74,0,.36,0,.73-.01,1.01h1.09ZM9.64,13.62c.74,0,1.07-.56,1.07-1.03,0-.55-.46-1.01-1.22-1.01h-.91c.01.43.01.79.01,1.18,0,.33,0,.34-.01.86h1.07Z" class="fill-primary"></path><path d="M15.3,16.28c-.46,0-1.19.02-1.62.04v-1.02s.53-.03.99-.05v-2.96h-1.03c.02-.11.03-.27.03-.4,0-.2-.02-.41-.03-.44.71-.06,1.33-.23,1.96-.5h.42v4.3l.82.05v1.02c-.42-.02-1.14-.04-1.54-.04Z" class="fill-primary"></path>',
  B2: '<path d="M5.86,8.72c0-.65-.09-2.09-.13-2.41h3.67c1.96,0,3.04,1.12,3.04,2.33,0,.95-.35,1.52-1.2,1.96v.03c1.01.44,1.47,1.25,1.47,2.08,0,1.47-1.16,2.73-3.11,2.73h-3.78s.04-1.07.04-1.55v-5.17ZM9.3,9.89c.64,0,.95-.43.95-.85,0-.57-.36-.91-1.09-.91h-.95c.01.32.01.43.01.74,0,.36,0,.73-.01,1.01h1.09ZM9.27,13.62c.74,0,1.07-.56,1.07-1.03,0-.55-.46-1.01-1.22-1.01h-.91c.01.43.01.79.01,1.18,0,.33,0,.34-.01.86h1.07Z" class="fill-primary"></path><path d="M13.34,15.45c.55-.34,2.24-1.58,2.24-2.68,0-.51-.38-.73-.79-.73-.39,0-.8.22-.99.6h-.05c-.1-.33-.44-.71-.56-.81.3-.4.92-.93,1.89-.93,1.3,0,1.93.71,1.93,1.71s-.8,1.86-1.78,2.65c.55-.04,1.05-.05,1.4-.05.2,0,.41,0,.53,0-.03.34-.03.73-.02,1.05-.06,0-1.18-.02-1.8-.02s-1.86.02-1.99.02v-.83Z" class="fill-primary"></path>',
  B3: '<path d="M6.06,8.72c0-.65-.09-2.09-.13-2.41h3.67c1.96,0,3.04,1.12,3.04,2.33,0,.95-.35,1.52-1.2,1.96v.03c1.01.44,1.47,1.25,1.47,2.08,0,1.47-1.16,2.73-3.11,2.73h-3.78s.04-1.07.04-1.55v-5.17ZM9.5,9.89c.64,0,.95-.43.95-.85,0-.57-.36-.91-1.09-.91h-.95c.01.32.01.43.01.74,0,.36,0,.73-.01,1.01h1.09ZM9.47,13.62c.74,0,1.07-.56,1.07-1.03,0-.55-.46-1.01-1.22-1.01h-.91c.01.43.01.79.01,1.18,0,.33,0,.34-.01.86h1.07Z" class="fill-primary"></path><path d="M15.62,14.54c0-.5-.56-.53-.99-.53h-.16c0-.07.02-.14.02-.21,0-.19-.04-.4-.09-.63.55,0,1.04-.18,1.04-.7,0-.31-.31-.44-.58-.44-.31,0-.68.16-.86.5h-.07c-.11-.33-.46-.71-.58-.8.3-.4.92-.83,1.88-.83s1.61.55,1.61,1.3c0,.45-.24.96-.82,1.21.68.2,1.02.59,1.02,1.28,0,.99-.87,1.62-1.91,1.62-.67,0-1.36-.23-1.74-.5.12-.28.2-.64.23-1.03.27.19.61.43,1.28.43.48,0,.72-.31.72-.67Z" class="fill-primary"></path>',
  V: '<path d="M11.32,13.21c.32-.9,1.3-4.67,1.92-6.98.75.03,1.64.05,2.69,0v.09c-1.4,4.01-2.82,7.74-3.28,9.21-.49-.03-.94-.03-1.39-.03s-.93,0-1.47.03c-.45-1.56-1.83-5.51-2.66-7.85-.21-.58-.38-1.06-.52-1.38v-.08c.58.03,1.81.07,2.74,0,0,0,1.63,6.08,1.97,6.98Z" class="fill-primary"></path>',
  V1: '<path d="M9.38,13.17c.31-.88,1.27-4.59,1.89-6.85.74.03,1.61.05,2.64,0v.09c-1.38,3.94-2.77,7.6-3.22,9.05-.48-.03-.92-.03-1.36-.03s-.91,0-1.44.03c-.44-1.53-1.79-5.41-2.61-7.71-.21-.57-.38-1.04-.51-1.35v-.08c.57.03,1.78.06,2.69,0,0,0,1.6,5.97,1.94,6.85Z" class="fill-primary"></path><path d="M15.85,16.28c-.46,0-1.19.02-1.62.04v-1.02s.53-.03.99-.05v-2.96h-1.03c.02-.11.03-.27.03-.4,0-.2-.02-.41-.03-.44.71-.06,1.33-.23,1.96-.5h.42v4.3l.82.05v1.02c-.42-.02-1.14-.04-1.54-.04Z" class="fill-primary"></path>',
  V2: '<path d="M9.02,13.17c.31-.88,1.27-4.59,1.89-6.85.74.03,1.61.05,2.64,0v.09c-1.38,3.94-2.77,7.6-3.22,9.05-.48-.03-.92-.03-1.37-.03s-.91,0-1.44.03c-.44-1.53-1.79-5.41-2.61-7.71-.21-.57-.38-1.04-.51-1.35v-.08c.57.03,1.78.06,2.69,0,0,0,1.6,5.97,1.94,6.85Z" class="fill-primary"></path><path d="M13.89,15.45c.55-.34,2.24-1.58,2.24-2.68,0-.51-.38-.73-.79-.73-.39,0-.8.22-.99.6h-.05c-.1-.33-.44-.71-.56-.81.3-.4.92-.93,1.89-.93,1.3,0,1.93.71,1.93,1.71s-.8,1.86-1.78,2.65c.55-.04,1.05-.05,1.4-.05.2,0,.41,0,.53,0-.03.34-.03.73-.02,1.05-.06,0-1.18-.02-1.8-.02s-1.86.02-1.99.02v-.83Z" class="fill-primary"></path>',
  V3: '<path d="M9.22,13.17c.31-.88,1.27-4.59,1.89-6.85.74.03,1.61.05,2.64,0v.09c-1.38,3.94-2.77,7.6-3.22,9.05-.48-.03-.92-.03-1.37-.03s-.91,0-1.44.03c-.44-1.53-1.79-5.41-2.61-7.71-.21-.57-.38-1.04-.51-1.35v-.08c.57.03,1.78.06,2.69,0,0,0,1.6,5.97,1.94,6.85Z" class="fill-primary"></path><path d="M16.17,14.54c0-.5-.56-.53-.99-.53h-.16c0-.07.02-.14.02-.21,0-.19-.04-.4-.09-.63.55,0,1.04-.18,1.04-.7,0-.31-.31-.44-.58-.44-.31,0-.68.16-.86.5h-.07c-.11-.33-.46-.71-.58-.8.3-.4.92-.83,1.88-.83s1.61.55,1.61,1.3c0,.45-.24.96-.82,1.21.68.2,1.02.59,1.02,1.28,0,.99-.87,1.62-1.91,1.62-.67,0-1.36-.23-1.74-.5.12-.28.2-.64.23-1.03.27.19.61.43,1.28.43.48,0,.72-.31.72-.67Z" class="fill-primary"></path>',
  ok: '<path d="M6.7,14.38c-1.92,0-3.4-1.13-3.4-3.62,0-2.34,1.52-3.69,3.43-3.69s3.4,1.13,3.4,3.63c0,2.34-1.52,3.68-3.43,3.68ZM6.72,8.56c-.94,0-1.47.93-1.47,2.14s.55,2.19,1.48,2.19,1.52-.93,1.52-2.14-.59-2.19-1.53-2.19Z" class="fill-primary"></path><path d="M11.26,8.97c0-.51-.07-1.6-.1-1.84.75,0,1.82-.06,1.99-.06-.01.27-.03.95-.03,1.33v2c.5-.75,1.88-2.81,2.11-3.27.72,0,1.44.02,2.16-.04v.07l-2.42,3.38c.36.51,1.87,2.7,2.57,3.63v.14c-.47,0-1.56.04-2.15.06-.56-.85-1.71-2.57-2.27-3.43v1.53c0,.51.06,1.6.09,1.84-.79,0-1.95.06-1.98.06,0-.02.03-.95.03-1.33v-4.08Z" class="fill-primary"></path>',
};

const pathsForIcon = (iconPath) => ({
  V: `${iconPath}kadet-v.png`,
  B: `${iconPath}kadet-b.png`,
  ok: `${iconPath}kadet-ok.png`,
});
